import { ImgHTMLAttributes } from "react";

type PictureData = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

interface ResponsiveImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> {
  source: PictureData | string;
  alt: string;
  sizes?: string;
}

/**
 * Renders a <picture> with WebP + JPG fallback and a responsive srcset.
 * Use with: import bmw from "@/assets/gallery/bmw.jpg?responsive";
 */
const ResponsiveImage = ({
  source,
  alt,
  sizes = "(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw",
  loading = "lazy",
  decoding = "async",
  className,
  ...rest
}: ResponsiveImageProps) => {
  if (typeof source === "string") {
    return (
      <img
        src={source}
        alt={alt}
        loading={loading}
        decoding={decoding}
        className={className}
        {...rest}
      />
    );
  }

  return (
    <picture>
      {Object.entries(source.sources).map(([type, srcset]) => (
        <source key={type} srcSet={srcset} type={type} sizes={sizes} />
      ))}
      <img
        src={source.img.src}
        width={source.img.w}
        height={source.img.h}
        alt={alt}
        loading={loading}
        decoding={decoding}
        className={className}
        {...rest}
      />
    </picture>
  );
};

export default ResponsiveImage;
